/**
 * OrganizrManager.ts - OrganizrManager - Universal list and category management
 * 
 * CHANGE HISTORY:
 * - 251130_1612 danielbeer: Added list registry and Outlinr integration APIs
 * - 251021_0509 [c090082] danielbeer: feat: complete remaining TypeScript fixes across all packages
 * - 251021_0452 [97a0397] danielbeer: fix: correct automated type annotations and test scripts
 * - 251021_0447 [ce7b5e6] danielbeer: feat: automate TypeScript and Actr integration for all packages
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { nanoid } from 'nanoid';
import type {
  ListData,
  ListItem,
  ListRegistry,
  LoadListRegistryOptions,
  LoadListOptions,
  SaveListOptions,
  ListOperationResult,
  OutlinerConversionOptions
} from './types';
import {
  convertToOutlinerNodes,
  flattenNodes,
  convertFromOutlinerNodes,
  validateNodesForConversion
} from './utils/outliner-conversion';


interface OrganizrManagerOptions {
  actr?: any; // Actr instance
  [key: string]: any;
}

export class OrganizrManager {
  private actr: any;
  private lists: Map<string, any>;
  private history: any[];
  private historyIndex: number;
  private maxHistory: number;
  private listeners: Set<any>;

  constructor(options: OrganizrManagerOptions = {}) {
    // Get Actr if provided, otherwise try to get it from Dependr
    this.actr = options.actr || this._tryGetActr();
    
    if (!this.actr) {
      throw new Error('Organizr requires Actr instance. Please provide actr in options or ensure @opcentral/actr is available.');
    }
    
    this.lists = new Map();
    this.history = [];
    this.historyIndex = -1;
    this.maxHistory = options.maxHistory || 50;
    this.listeners = new Set();
    
    // Load from storage if storage data is provided (not just options)
    if (options && options.storageData) {
      this.loadFromStorage(options.storageData);
    }
    
    // Register actions with Actr
    this.registerActions();
  }
  
  /**
   * Try to get Actr from Dependr (optional)
   */
  private _tryGetActr() {
    try {
      const { getActr } = require('@opcentral/actr');
      return getActr();
    } catch {
      return null;
    }
  }
  
  /**
   * Register Organizr actions with Actr
   * 
   * ARCHITECTURE NOTE: Organizr uses FULL CRUD pattern
   * 
   * Why Full CRUD:
   * 1. Persistent Lists: Lists/categories are database entities
   * 2. List Entity Management: Full lifecycle (create, read, update, delete)
   * 3. Relationship Management: Lists have hierarchies and relationships
   * 
   * Pattern: Full CRUD (Standard)
   * - Uses actr.create/read/update/delete for all list operations
   * - Data persisted through Aggregatr via Actr
   * - Automatic logging, permissions, and middleware
   */
  private registerActions(): void {
    if (!this.actr) return;
    
    // Register custom actions for list operations
    this.actr.registerAction('organizr:add-item', async (params: { listId: string; itemData: any; parentId?: string }) => {
      return await this.addItem(params.listId, params.itemData, params.parentId);
    });
    
    this.actr.registerAction('organizr:update-item', async (params: { listId: string; itemId: string; updates: any }) => {
      return await this.updateItem(params.listId, params.itemId, params.updates);
    });
    
    this.actr.registerAction('organizr:delete-item', async (params: { listId: string; itemId: string }) => {
      return await this.deleteItem(params.listId, params.itemId);
    });
    
    this.actr.registerAction('organizr:move-item', async (params: { listId: string; itemId: string; newParentId?: string }) => {
      return await this.moveItem(params.listId, params.itemId, params.newParentId);
    });
    
    this.actr.registerAction('organizr:reorder-items', async (params: { listId: string; itemIds: string[]; parentId?: string }) => {
      return await this.reorderItems(params.listId, params.itemIds, params.parentId);
    });
  }
  
  /**
   * Create a new list
   */
  async createList(data: any): Promise<any> {
    const listData = {
      id: data.id || nanoid(),
      name: data.name || 'New List',
      type: 'list',
      color: data.color || '#6b7280',
      pattern: data.pattern || 'solid',
      icon: data.icon || '📋',
      defaultIcon: data.defaultIcon || null,
      allowOther: data.allowOther !== false,
      children: data.children || [],
      metadata: data.metadata || {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    // Create via Actr (which delegates to Aggregatr)
    const list = await this.actr.create('organizr-list', listData);
    
    // Cache locally
    this.lists.set(list.id, list);
    this.recordChange('create', list.id, null, list);
    this.notifyListeners('create', list);
    
    return list;
  }
  
  /**
   * Get a list by ID
   */
  async getList(listId: string): Promise<any> {
    // Check cache first
    const cached = this.lists.get(listId);
    if (cached) return cached;
    
    // Read from Actr/Aggregatr
    const list = await this.actr.read('organizr-list', listId);
    
    // Cache if found
    if (list) {
      this.lists.set(listId, list);
    }
    
    return list;
  }
  
  /**
   * Get all lists
   */
  async getAllLists(): Promise<any[]> {
    // Query all lists from Actr/Aggregatr
    const lists = await this.actr.query('organizr-list', {});
    
    // Update cache
    lists.forEach((list: any) => {
      this.lists.set(list.id, list);
    });
    
    return lists;
  }
  
  /**
   * Update a list
   */
  async updateList(listId: string, updates: any): Promise<any> {
    // Get current list
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    const oldList = { ...list };
    
    // Update via Actr (which delegates to Aggregatr)
    const updatedData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    const updatedList = await this.actr.update('organizr-list', listId, updatedData);
    
    // Update cache
    this.lists.set(listId, updatedList);
    this.recordChange('update', listId, oldList, updatedList);
    this.notifyListeners('update', updatedList, oldList);
    
    return updatedList;
  }
  
  /**
   * Delete a list
   */
  async deleteList(listId: string): Promise<boolean> {
    // Get list before deleting
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    // Delete via Actr (which delegates to Aggregatr)
    const deleted = await this.actr.delete('organizr-list', listId);
    
    // Remove from cache
    this.lists.delete(listId);
    this.recordChange('delete', listId, list, null);
    this.notifyListeners('delete', list);
    
    return deleted;
  }
  
  /**
   * Add item to list
   */
  async addItem(listId: string, itemData: any, parentId: string | null = null): Promise<any> {
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    const item = {
      id: itemData.id || nanoid(),
      name: itemData.name || 'New Item',
      value: itemData.value || itemData.name?.toLowerCase().replace(/\s+/g, '-'),
      color: itemData.color || list.color,
      icon: itemData.icon || list.defaultIcon || null,
      pattern: itemData.pattern || list.pattern,
      order: itemData.order ?? (list.children.length),
      children: itemData.children || [],
      metadata: itemData.metadata || {}
    };
    
    if (parentId) {
      // Add to nested parent
      const parent = this.findItemById(list, parentId);
      if (!parent) throw new Error(`Parent item ${parentId} not found`);
      
      if (!parent.children) parent.children = [];
      parent.children.push(item);
    } else {
      // Add to root
      list.children.push(item);
    }
    
    // Update list via Actr
    const updatedList = await this.updateList(listId, { children: list.children });
    
    this.recordChange('addItem', listId, null, { list: updatedList, item });
    this.notifyListeners('addItem', updatedList, item);
    
    return item;
  }
  
  /**
   * Update item in list
   */
  async updateItem(listId: string, itemId: string, updates: any): Promise<any> {
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    const item = this.findItemById(list, itemId);
    if (!item) throw new Error(`Item ${itemId} not found`);
    
    const oldItem = { ...item };
    Object.assign(item, updates);
    
    // Update list via Actr
    const updatedList = await this.updateList(listId, { children: list.children });
    
    this.recordChange('updateItem', listId, oldItem, item);
    this.notifyListeners('updateItem', updatedList, item, oldItem);
    
    return item;
  }
  
  /**
   * Delete item from list
   */
  async deleteItem(listId: string, itemId: string): Promise<boolean> {
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    const result = this.removeItemById(list, itemId);
    if (!result) throw new Error(`Item ${itemId} not found`);
    
    // Update list via Actr
    const updatedList = await this.updateList(listId, { children: list.children });
    
    this.recordChange('deleteItem', listId, result, null);
    this.notifyListeners('deleteItem', updatedList, result);
    
    return true;
  }
  
  /**
   * Reorder items in list
   */
  async reorderItems(listId: string, itemIds: string[], parentId: string | null = null): Promise<boolean> {
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    const container = parentId 
      ? this.findItemById(list, parentId)
      : list;
    
    if (!container) throw new Error(`Container not found`);
    
    const oldOrder = container.children.map((item: any) => item.id);
    
    // Reorder based on itemIds array
    container.children.sort((a: any, b: any) => {
      const indexA = itemIds.indexOf(a.id);
      const indexB = itemIds.indexOf(b.id);
      return indexA - indexB;
    });
    
    // Update order property
    container.children.forEach((item: any, index: number) => {
      item.order = index;
    });
    
    // Update list via Actr
    const updatedList = await this.updateList(listId, { children: list.children });
    
    this.recordChange('reorder', listId, oldOrder, itemIds);
    this.notifyListeners('reorder', updatedList);
    
    return true;
  }
  
  /**
   * Move item to different parent
   */
  async moveItem(listId: string, itemId: string, newParentId: string | null = null): Promise<boolean> {
    const list = await this.getList(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    // Remove from current location
    const item = this.removeItemById(list, itemId);
    if (!item) throw new Error(`Item ${itemId} not found`);
    
    // Add to new location
    if (newParentId) {
      const newParent = this.findItemById(list, newParentId);
      if (!newParent) throw new Error(`New parent ${newParentId} not found`);
      
      if (!newParent.children) newParent.children = [];
      newParent.children.push(item);
    } else {
      list.children.push(item);
    }
    
    // Update list via Actr
    const updatedList = await this.updateList(listId, { children: list.children });
    
    this.recordChange('moveItem', listId, { itemId, oldParent: null }, { itemId, newParentId });
    this.notifyListeners('moveItem', updatedList, item);
    
    return true;
  }
  
  /**
   * Find item by ID (recursive)
   */
  findItemById(container, itemId): any {
    if (!container.children) return null;
    
    for (const item of container.children) {
      if (item.id === itemId) return item;
      
      if (item.children) {
        const found = this.findItemById(item, itemId);
        if (found) return found;
      }
    }
    
    return null;
  }
  
  /**
   * Remove item by ID (recursive)
   */
  removeItemById(container, itemId): any {
    if (!container.children) return null;
    
    const index = container.children.findIndex(item => item.id === itemId);
    if (index !== -1) {
      const [removed] = container.children.splice(index, 1);
      return removed;
    }
    
    for (const item of container.children) {
      if (item.children) {
        const removed = this.removeItemById(item, itemId);
        if (removed) return removed;
      }
    }
    
    return null;
  }
  
  /**
   * Get item display value
   */
  getItemDisplay(listId, itemId): any {
    const list = this.lists.get(listId);
    if (!list) return null;
    
    const item = this.findItemById(list, itemId);
    if (!item) return null;
    
    return {
      id: item.id,
      name: item.name,
      value: item.value,
      color: item.color,
      icon: item.icon,
      pattern: item.pattern
    };
  }
  
  /**
   * Get all items from list (flattened)
   */
  getAllItems(listId, includeNested = true): any {
    const list = this.lists.get(listId);
    if (!list) return [];
    
    const items = [];
    
    const traverse = (children, depth = 0) => {
      children.forEach(item => {
        items.push({ ...item, depth });
        
        if (includeNested && item.children) {
          traverse(item.children, depth + 1);
        }
      });
    };
    
    traverse(list.children);
    return items;
  }
  
  /**
   * Create list from field values (unique extraction)
   */
  createListFromField(name, values, options): any {
    const uniqueValues = [...new Set(values.filter(Boolean))];
    
    const children = uniqueValues.map((value, index) => ({
      id: nanoid(),
      name: String(value),
      value: String(value).toLowerCase().replace(/\s+/g, '-'),
      order: index,
      color: options.color || '#6b7280',
      icon: options.icon || null
    }));
    
    if (options) {
      children.push({
        id: nanoid(),
        name: 'Other...',
        value: '_other',
        order: children.length,
        color: '#9ca3af',
        icon: '➕'
      });
    }
    
    return this.createList({
      name,
      children,
      ...options
    });
  }
  
  /**
   * Import list from data
   */
  importList(data, format = 'json'): any {
    if (format === 'json') {
      if (typeof data) {
        data = JSON.parse(data);
      }
      
      return this.createList(data);
    }
    
    // For other formats, use Parsely
    throw new Error('Use Parsely to parse non-JSON formats first');
  }
  
  /**
   * Export list
   */
  exportList(listId, format = 'json'): any {
    const list = this.lists.get(listId);
    if (!list) throw new Error(`List ${listId} not found`);
    
    if (format === 'json') {
      return JSON.stringify(list, null, 2);
    }
    
    // For other formats, convert to simple array first
    const items = this.getAllItems(listId, true);
    return items;
  }
  
  /**
   * Undo/Redo support
   */
  recordChange(action, listId, oldValue, newValue): void {
    // Remove any history after current index
    this.history = this.history.slice(0, this.historyIndex + 1);
    
    // Add new change
    this.history.push({
      action,
      listId,
      oldValue,
      newValue,
      timestamp: Date.now()
    });
    
    // Limit history size
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    } else {
      this.historyIndex++;
    }
  }
  
  canUndo(): any {
    return this.historyIndex >= 0;
  }
  
  canRedo(): any {
    return this.historyIndex < this.history.length - 1;
  }
  
  undo(): any {
    if (!this.canUndo()) return false;
    
    const change = this.history[this.historyIndex];
    this.applyChange(change, true); // Reverse
    this.historyIndex--;
    
    return true;
  }
  
  redo(): any {
    if (!this.canRedo()) return false;
    
    this.historyIndex++;
    const change = this.history[this.historyIndex];
    this.applyChange(change, false); // Forward
    
    return true;
  }
  
  applyChange(change, reverse): void {
    const { action, listId, oldValue, newValue } = change;
    
    // Apply change in reverse or forward direction
    // Implementation depends on action type
    // This is a simplified version
    
    if (reverse) {
      // Undo logic
      switch (action) {
        case 'create':
          this.lists.delete(listId);
          break;
        case 'delete':
          this.lists.set(listId, oldValue);
          break;
        case 'update':
          this.lists.set(listId, oldValue);
          break;
      }
    } else {
      // Redo logic
      switch (action) {
        case 'create':
          this.lists.set(listId, newValue);
          break;
        case 'delete':
          this.lists.delete(listId);
          break;
        case 'update':
          this.lists.set(listId, newValue);
          break;
      }
    }
    
    this.notifyListeners('change', { action, reverse });
  }
  
  /**
   * Event listeners
   */
  subscribe(listener: Function): any {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
  
  notifyListeners(event, ...args: any[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, ...args);
      } catch (error) {
        console.error('Listener error:', error);
      }
    });
  }
  
  /**
   * Storage
   */
  toJSON(): any {
    return {
      lists: Array.from(this.lists.values()),
      version: '1.0.0'
    };
  }
  
  loadFromStorage(data): void {
    // If data is a string, try to parse it
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (error) {
        console.warn('[Organizr] Failed to parse storage data:', error);
        return;
      }
    }
    
    // Only load if data has lists array
    if (data && data.lists && Array.isArray(data.lists)) {
      data.lists.forEach(list => {
        this.lists.set(list.id, list);
      });
    }
  }
  
  saveToStorage(): any {
    return JSON.stringify(this.toJSON());
  }
  
  /**
   * ========================================
   * LIST REGISTRY & API INTEGRATION
   * ========================================
   * Methods for loading/saving lists from external APIs
   */
  
  /**
   * Load list registry from API
   * 
   * @param options - Load options
   * @returns List registry with metadata
   * 
   * @example
   * const registry = await organizr.loadListRegistry({
   *   includeItemCounts: true,
   *   apiEndpoint: '/api/lists/registry'
   * });
   */
  async loadListRegistry(options: LoadListRegistryOptions = {}): Promise<ListData[]> {
    const {
      includeItemCounts = false,
      apiEndpoint = '/api/lists/registry'
    } = options;
    
    try {
      // Fetch registry from API
      const response = await fetch(apiEndpoint);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const registry: ListRegistry = await response.json();
      
      // Convert registry to list array with metadata
      const lists: ListData[] = await Promise.all(
        Object.entries(registry.lists).map(async ([id, info]) => {
          // Try to fetch actual list to get item count if requested
          let itemCount = 0;
          if (includeItemCounts) {
            try {
              const listResponse = await fetch(`/api/lists/${info.file}`);
              if (listResponse.ok) {
                const listData = await listResponse.json();
                itemCount = listData.children?.length || 0;
              }
            } catch {
              // Ignore errors, use 0
            }
          }
          
          // Extract fields but exclude schema definitions from metadata
          // Schema fields have {type, required, indexed} structure
          const cleanMetadata: Record<string, any> = {};
          if (info.fields) {
            Object.entries(info.fields).forEach(([key, value]) => {
              // Only include simple values, not schema objects
              if (typeof value !== 'object' || value === null || Array.isArray(value)) {
                cleanMetadata[key] = value;
              } else if (!('type' in value && 'required' in value)) {
                // Include objects that aren't schema definitions
                cleanMetadata[key] = value;
              }
            });
          }
          
          // Generate display name from ID (e.g., "active-customers" -> "Active Customers")
          const displayName = id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          
          return {
            id,
            name: displayName,
            shortName: undefined,
            version: '1.0.0',
            type: info.type || 'fixed',
            description: info.description || '',
            source: info.source || info.file,
            lastUpdated: new Date().toISOString().split('T')[0],
            color: '#6b7280',
            pattern: 'solid',
            icon: info.type === 'dynamic' ? '🔄' : '📋',
            defaultIcon: null,
            allowOther: false,
            children: includeItemCounts ? Array(itemCount).fill({ id: '', name: '', value: '', color: '', icon: null, pattern: '', order: 0 }) : [],
            metadata: {}, // Don't include schema fields in metadata
            sourceFile: info.file
          } as ListData;
        })
      );
      
      return lists;
    } catch (error) {
      throw new Error(`Failed to load list registry: ${error}`);
    }
  }
  
  /**
   * Load a single list from API
   * 
   * @param listId - List ID to load
   * @param options - Load options
   * @returns List data
   * 
   * @example
   * const list = await organizr.loadListFromAPI('common-priorities');
   */
  async loadListFromAPI(listId: string, options: LoadListOptions = {}): Promise<ListData> {
    const { apiEndpoint = '/api/lists' } = options;
    
    try {
      // Fetch registry to get file path
      const registryResponse = await fetch(`${apiEndpoint}/registry`);
      if (!registryResponse.ok) {
        throw new Error(`HTTP ${registryResponse.status}: ${registryResponse.statusText}`);
      }
      const registry: ListRegistry = await registryResponse.json();
      const listInfo = registry.lists[listId];
      
      if (!listInfo) {
        throw new Error(`List ${listId} not found in registry`);
      }

      // Fetch the actual list file
      const listResponse = await fetch(`${apiEndpoint}/${listInfo.file}`);
      if (!listResponse.ok) {
        throw new Error(`HTTP ${listResponse.status}: ${listResponse.statusText}`);
      }
      const rawListData = await listResponse.json();
      
      // Sanitize the list data to ensure all fields are safe for React rendering
      const listData: ListData = {
        id: rawListData.id,
        name: rawListData.name || rawListData.id,
        shortName: rawListData.shortName,
        version: rawListData.version || '1.0.0',
        type: rawListData.type || 'fixed',
        description: rawListData.description || '',
        source: rawListData.source || '',
        lastUpdated: rawListData.lastUpdated || new Date().toISOString().split('T')[0],
        color: rawListData.color || '#6b7280',
        pattern: rawListData.pattern || 'solid',
        icon: rawListData.icon || '📋',
        defaultIcon: rawListData.defaultIcon || null,
        allowOther: Boolean(rawListData.allowOther),
        children: Array.isArray(rawListData.children) ? rawListData.children : [],
        metadata: {}, // Don't include raw metadata
        tags: Array.isArray(rawListData.tags) ? rawListData.tags : undefined,
        // Stringify object fields to prevent React rendering errors
        query: typeof rawListData.query === 'object' ? JSON.stringify(rawListData.query) : rawListData.query,
        refresh: typeof rawListData.refresh === 'object' ? JSON.stringify(rawListData.refresh) : rawListData.refresh,
        ttl: typeof rawListData.ttl === 'number' ? rawListData.ttl : undefined,
        sourceFile: rawListData.sourceFile,
        sourceUrl: rawListData.sourceUrl
      };
      
      return listData;
    } catch (error) {
      throw new Error(`Failed to load list: ${error}`);
    }
  }
  
  /**
   * Save a list to API
   * 
   * @param listId - List ID to save
   * @param data - List data to save
   * @param options - Save options
   * @returns Operation result
   * 
   * @example
   * await organizr.saveListToAPI('common-priorities', listData);
   */
  async saveListToAPI(listId: string, data: ListData, options: SaveListOptions = {}): Promise<ListOperationResult> {
    const { apiEndpoint = '/api/lists' } = options;
    
    try {
      // Get file path from registry
      const registryResponse = await fetch(`${apiEndpoint}/registry`);
      if (!registryResponse.ok) {
        throw new Error(`HTTP ${registryResponse.status}: ${registryResponse.statusText}`);
      }
      const registry: ListRegistry = await registryResponse.json();
      const listInfo = registry.lists[listId];
      
      if (!listInfo) {
        throw new Error('List not found in registry');
      }
      
      // Save to API
      const response = await fetch(`${apiEndpoint}/${listInfo.file}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return {
        success: true,
        message: 'List saved successfully'
      };
    } catch (error) {
      return {
        success: false,
        error: `Failed to save list: ${error}`
      };
    }
  }
  
  /**
   * ========================================
   * OUTLINR INTEGRATION
   * ========================================
   * Methods for converting between Organizr lists and Outlinr nodes
   */
  
  /**
   * Convert list items to Outlinr nodes (hierarchical format)
   * 
   * @param items - List items to convert
   * @param options - Conversion options
   * @returns Outlinr nodes
   * 
   * @example
   * const nodes = organizr.toOutlinerNodes(list.children);
   */
  toOutlinerNodes(items: ListItem[], options: OutlinerConversionOptions = {}): any[] {
    return convertToOutlinerNodes(items, null, 0, options);
  }
  
  /**
   * Flatten list items to Outlinr nodes (flat array format)
   * 
   * This is the format Outlinr expects for editing
   * 
   * @param items - List items to flatten
   * @param options - Conversion options
   * @returns Flattened Outlinr nodes
   * 
   * @example
   * const nodes = organizr.flattenToOutlinerNodes(list.children);
   */
  flattenToOutlinerNodes(items: ListItem[], options: OutlinerConversionOptions = {}): any[] {
    return flattenNodes(items, null, 0, options);
  }
  
  /**
   * Convert Outlinr nodes back to list items
   * 
   * @param nodes - Outlinr nodes to convert
   * @param defaultColor - Default color for items
   * @param defaultPattern - Default pattern for items
   * @returns List items
   * 
   * @example
   * const items = organizr.fromOutlinerNodes(nodes, '#6b7280', 'solid');
   */
  fromOutlinerNodes(nodes: any[], defaultColor?: string, defaultPattern?: string): ListItem[] {
    return convertFromOutlinerNodes(nodes, defaultColor, defaultPattern);
  }
  
  /**
   * Validate nodes before conversion
   * 
   * @param nodes - Nodes to validate
   * @returns Validation result
   * 
   * @example
   * const validation = organizr.validateOutlinerNodes(nodes);
   * if (!validation.valid) {
   *   console.error('Validation errors:', validation.errors);
   * }
   */
  validateOutlinerNodes(nodes: any[]): { valid: boolean; errors: string[] } {
    return validateNodesForConversion(nodes);
  }
}

export default OrganizrManager;
